<mat-sidenav-container class="messaging-container">
  <!-- Sidebar -->
  <mat-sidenav mode="side" opened class="sidebar" aria-label="Conversations sidebar">
    <!--Side Bar Header-->
    <mat-toolbar color="primary">
      <span>Messages</span>
      <span class="spacer"></span>
      <button
        mat-icon-button
        matTooltip="New message"
        (click)="openNewConversationDialog()"
        class="chat-list-header-buttons"
        data-testid="new-conversation-button"
      >
        <mat-icon>add</mat-icon>
      </button>
      <button
        mat-icon-button
        matTooltip="Settings"
        class="chat-list-header-buttons"
        data-testid="sidebar-menu-settings-button"
      >
        <mat-icon>settings</mat-icon>
      </button>
    </mat-toolbar>

    <!-- Search -->
    <div class="search-wrapper">
      <div class="cd-filter-field">
        <div class="cd-search-input-wrap">
          <mat-icon class="cd-search-icon" aria-hidden="true">search</mat-icon>
          <input
            class="cd-filter-input cd-search-input"
            type="text"
            placeholder="Search chats..."
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchInput($event)"
            aria-label="Search conversations"
            autocomplete="off"
            data-testid="search-input"
          />
          @if (searchQuery) {
            <button
              class="cd-search-clear"
              (click)="clearSearch()"
              aria-label="Clear search"
              data-testid="clear-search-button"
            >
              <mat-icon aria-hidden="true">close</mat-icon>
            </button>
          }
        </div>
      </div>
    </div>

    @if (searchQuery.trim() && visibleChats.length === 0 && !isSearching) {
      <div class="no-results" role="status" aria-live="polite">
        <mat-icon aria-hidden="true">search_off</mat-icon>
        <span>No conversations found</span>
      </div>
    }

    <!--List of Chats-->
    <mat-nav-list class="chat-list">
      @for (chat of visibleChats; track chat.id) {
        <mat-list-item
          class="chat-item"
          (click)="selectChat(chat)"
          [class.active]="selectedChat?.id === chat.id"
          [matTooltip]="chat.name"
          matTooltipPosition="right"
          [attr.data-testid]="'chat-list-item-' + chat.id"
        >
          <div matListItemAvatar class="avatar-wrapper">
            <div class="avatar-circle">
              <app-user-avatar [imageUrl]="chat.imageUrl" [name]="chat.name" />
            </div>
            @if (!chat.is_group) {
              <span
                class="presence-dot"
                [class.online]="isUserOnline(chat.userId)"
                [class.offline]="!isUserOnline(chat.userId)"
              >
              </span>
            }
            @if (chat.unread) {
              <span
                class="avatar-unread-dot"
                aria-hidden="true"
                [attr.data-testid]="'avatar-unread-dot-' + chat.id"
              ></span>
            }
          </div>

          <span matListItemTitle>{{ chat.name }}</span>
          <span matListItemLine class="last-message">
            @if (chat.typingUserIds?.length) {
              <em aria-label="{{ chat.name }} is typing">typing...</em>
            } @else if (chat.matchSnippet) {
              <span
                class="match-snippet"
                [innerHTML]="chat.matchSnippet | highlight: searchQuery"
              ></span>
            } @else {
              {{ chat.lastMessage || 'No messages yet' }}
            }
          </span>

          <span matListItemMeta class="chat-meta">
            @if (chat.muted) {
              <mat-icon class="muted-icon" aria-label="Muted" matTooltip="Notifications muted">notifications_off</mat-icon>
            }
            <span class="chat-time">{{ formatTime(chat.time) }}</span>
            @if (chat.matchCount && chat.matchCount > 1) {
              <span
                class="match-count-badge"
                aria-label="{{ chat.matchCount }} matching messages"
                [attr.data-testid]="'match-count-badge-' + chat.id"
              >
                {{ chat.matchCount }}
              </span>
            }
          </span>
          <button
            class="leave-chat-btn"
            (click)="leaveConversation(chat); $event.stopPropagation()"
            [matTooltip]="chat.is_group ? 'Leave conversation' : 'Hide conversation'"
            [attr.aria-label]="(chat.is_group ? 'Leave ' : 'Hide ') + chat.name"
            [attr.data-testid]="'leave-chat-button-' + chat.id"
          >
            <mat-icon>close</mat-icon>
          </button>
        </mat-list-item>
      }
    </mat-nav-list>
  </mat-sidenav>

  <!-- Main Chat Panel -->
  <mat-sidenav-content
    class="chat-window"
    (dragenter)="onDragEnter($event)"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onFilesDropped($event)"
  >
    @if (isDraggingOver) {
      <div class="attachment-drop-overlay" data-testid="attachment-drop-overlay">
        <mat-icon>attach_file</mat-icon>
        <span>Drop files to attach</span>
      </div>
    }
    <ng-container *ngIf="selectedChat; else noChat">
      <!-- Chat Header -->
      <mat-toolbar class="chat-header">
        <app-user-link
          [userId]="selectedChat.is_group ? null : selectedChat.userId"
          [ariaLabel]="'View ' + selectedChat.name + '\'s profile'"
        >
          <div class="avatar-circle" aria-hidden="true">
            <app-user-avatar [imageUrl]="selectedChat.imageUrl" [name]="selectedChat.name" />
          </div>
        </app-user-link>
        <div class="chat-header-info">
          @if (editingConversationName) {
            <input
              class="chat-name-input"
              [(ngModel)]="conversationNameDraft"
              (keydown.enter)="saveConversationName()"
              (keydown.escape)="cancelEditConversationName()"
              (blur)="saveConversationName()"
              aria-label="Conversation name"
              data-testid="chat-name-input"
            />
          } @else {
            <span
              class="chat-name"
              [class.chat-name-editable]="canRenameConversation()"
              [matTooltip]="canRenameConversation() ? 'Click to rename' : ''"
              (click)="startEditConversationName()"
              data-testid="chat-name"
            >
              {{ selectedChat.name }}
              @if (canRenameConversation()) {
                <mat-icon class="chat-name-edit-icon" aria-hidden="true">edit</mat-icon>
              }
            </span>
          }
          @if (!selectedChat.is_group) {
            <span
              class="chat-status"
              aria-label="{{ selectedChat.name }} is online"
              [class.online]="isUserOnline(selectedChat.userId)"
              [class.offline]="!isUserOnline(selectedChat.userId)"
            >
              @if (isUserOnline(selectedChat.userId)) {
                Online
              } @else {
                Offline
              }
            </span>
          }
        </div>
        @if (selectedChat.is_group) {
          <button mat-icon-button (click)="openAddMembersDialog()" data-testid="add-members-button">
            <mat-icon>add</mat-icon>
          </button>
        }
        <span class="spacer"></span>
        <button
          mat-icon-button
          (click)="toggleMuted()"
          [matTooltip]="selectedChat.muted ? 'Unmute notifications' : 'Mute notifications'"
          [attr.aria-label]="selectedChat.muted ? 'Unmute notifications for ' + selectedChat.name : 'Mute notifications for ' + selectedChat.name"
          data-testid="mute-conversation-button"
        >
          <mat-icon aria-hidden="true">{{ selectedChat.muted ? 'notifications_off' : 'notifications' }}</mat-icon>
        </button>
        <button
          *hasPermission="permissions.MESSAGING.EXPORT_TRANSCRIPT"
          mat-icon-button
          (click)="downloadTranscript()"
          matTooltip="Download Transcript"
          aria-label="Download transcript for {{ selectedChat.name }}"
          data-testid="download-transcript-button"
        >
          <mat-icon aria-hidden="true">download</mat-icon>
        </button>
        @if (!selectedChat.is_group) {
          <button
            mat-icon-button
            [matMenuTriggerFor]="chatMenu"
            matTooltip="More options"
            aria-label="More options for {{ selectedChat.name }}"
            aria-haspopup="true"
            data-testid="chat-header-menu-button"
          >
            <mat-icon aria-hidden="true">more_vert</mat-icon>
          </button>
          <mat-menu #chatMenu="matMenu" panelClass="messages-menu">
            <button mat-menu-item data-testid="chat-menu-view-profile-button">
              <mat-icon aria-hidden="true">person</mat-icon>
              <span>View Profile</span>
            </button>
          </mat-menu>
        }
      </mat-toolbar>

      <mat-divider></mat-divider>

      <div class="messages-pane">
        <mat-card
          class="chat-match-nav"
          *ngIf="!matchNavDismissed && (selectedChat.matchingMessageIds?.length ?? 0) > 1"
          data-testid="match-nav"
        >
          <span class="match-nav-label">
            Searching for: <strong>{{ searchQuery }}</strong>
          </span>

          <span class="match-nav-count">
            {{ currentMatchIndex + 1 }} of {{ selectedChat.matchingMessageIds?.length }}
          </span>

          <button
            mat-icon-button
            aria-label="previous"
            (click)="jumpPrev(selectedChat)"
            data-testid="match-nav-prev-button"
          >
            <mat-icon>keyboard_arrow_up</mat-icon>
          </button>
          <button
            mat-icon-button
            aria-label="next"
            (click)="jumpNext(selectedChat)"
            data-testid="match-nav-next-button"
          >
            <mat-icon>keyboard_arrow_down</mat-icon>
          </button>

          <button
            mat-icon-button
            aria-label="close"
            (click)="closeSearchPopup()"
            data-testid="match-nav-close-button"
          >
            <mat-icon>close</mat-icon>
          </button>
        </mat-card>

        <!-- Messages -->
        <div
          class="messages-area"
          #scrollContainer
          (scroll)="onMessagesScroll()"
          role="log"
          aria-live="polite"
          aria-label="Messages"
        >
          <ng-container *ngFor="let message of selectedChat.messages; let i = index">
            @if (getDateDividerLabel(i); as dividerLabel) {
              <div class="date-divider" role="separator" [attr.aria-label]="dividerLabel">
                <span>{{ dividerLabel }}</span>
              </div>
            }
            <div
              class="message-wrapper"
              [id]="'message-' + message.id"
              [class.sent]="message.fromMe && !message.is_system"
              [class.received]="!message.fromMe && !message.is_system"
              [class.system]="message.is_system"
              [class.pending]="message.status === 'pending'"
              [class.highlighted]="message.id === highlightedMessageId"
              role="article"
              [attr.aria-label]="
                (message.fromMe ? 'You' : selectedChat.name) +
                ': ' +
                (message.is_deleted ? 'This message was deleted' : message.content)
              "
              [attr.data-testid]="'message-' + message.id"
            >
              <mat-card class="message-card" appearance="outlined">
                @if (!message.is_system) {
                  <div class="message-sender-row">
                    <span class="message-sender-name">
                      {{ getSenderName(message) }}
                    </span>
                  </div>
                }
                <mat-card-content>
                  @if (message.replyTo) {
                    <div
                      class="reply-quote"
                      [class.deleted]="message.replyTo.is_deleted"
                      [class.clickable]="!message.replyTo.is_deleted"
                      [attr.role]="message.replyTo.is_deleted ? null : 'button'"
                      [attr.tabindex]="message.replyTo.is_deleted ? null : 0"
                      (click)="message.replyTo.is_deleted || jumpToMessage(message.replyTo.id)"
                      (keydown.enter)="
                        message.replyTo.is_deleted || jumpToMessage(message.replyTo.id)
                      "
                      [attr.data-testid]="'reply-quote-' + message.id"
                    >
                      <span class="reply-quote-sender">{{
                        getReplyQuoteSenderName(message.replyTo)
                      }}</span>
                      <span class="reply-quote-snippet">{{
                        getReplyQuoteSnippet(message.replyTo)
                      }}</span>
                    </div>
                  }
                  @if (message.is_deleted) {
                    <p class="deleted-message">This message was deleted</p>
                  } @else {
                    @if (message.attachments?.length) {
                      <div
                        class="attachment-grid"
                        [class.single-image]="isSingleImageMessage(message)"
                      >
                        @for (attachment of message.attachments; track attachment.url) {
                          @if (isImageAttachment(attachment)) {
                            <img
                              class="attachment-thumb"
                              [src]="attachment.url"
                              [alt]="attachment.file_name"
                              (click)="onAttachmentClick(message, attachment)"
                              (load)="onAttachmentImageLoad()"
                            />
                          } @else {
                            <button
                              type="button"
                              class="attachment-chip"
                              (click)="onAttachmentClick(message, attachment)"
                            >
                              <mat-icon>{{ getAttachmentIcon(attachment) }}</mat-icon>
                              <span class="attachment-chip-name">{{ attachment.file_name }}</span>
                            </button>
                          }
                        }
                      </div>
                    }
                    @if (editingMessageId === message.id) {
                      <div class="edit-message-box">
                        <input
                          class="edit-message-input"
                          [(ngModel)]="editMessageText"
                          (keydown.enter)="saveEditedMessage(message)"
                          (keydown.escape)="cancelEditMessage()"
                          [attr.data-testid]="'edit-message-input-' + message.id"
                        />
                        <div class="edit-actions">
                          <button
                            mat-icon-button
                            (click)="saveEditedMessage(message)"
                            [attr.data-testid]="'save-edited-message-button-' + message.id"
                          >
                            <mat-icon>check</mat-icon>
                          </button>
                          <button
                            mat-icon-button
                            (click)="cancelEditMessage()"
                            [attr.data-testid]="'cancel-edit-message-button-' + message.id"
                          >
                            <mat-icon>close</mat-icon>
                          </button>
                        </div>
                      </div>
                    } @else {
                      @if (message.is_system) {
                        <div class="system-message">
                          {{ message.content }}
                        </div>
                      } @else {
                        <p
                          [innerHTML]="
                            message.content
                              | highlight: searchQuery : message.id === activeMatchMessageId
                          "
                        ></p>

                        @if (message.is_edited) {
                          <span class="edited-label"> (edited) </span>
                        }
                      }
                    }
                  }
                  @if (!message.is_deleted && !message.is_system && !message.status) {
                    <div class="message-actions">
                      <button
                        mat-icon-button
                        (click)="startReply(message)"
                        [attr.data-testid]="'reply-message-button-' + message.id"
                        matTooltip="Reply"
                      >
                        <mat-icon> reply </mat-icon>
                      </button>

                      @if (message.fromMe) {
                        <button
                          mat-icon-button
                          (click)="startEditMessage(message)"
                          [attr.data-testid]="'edit-message-button-' + message.id"
                        >
                          <mat-icon> edit </mat-icon>
                        </button>

                        <button
                          mat-icon-button
                          (click)="deleteMessage(message)"
                          [attr.data-testid]="'delete-message-button-' + message.id"
                        >
                          <mat-icon> delete </mat-icon>
                        </button>
                      }
                    </div>
                  }
                  <div class="message-meta">
                    <span class="timestamp">{{ formatTime(message.created_at) }}</span>
                    @if (message.status === 'pending') {
                      <span
                        class="send-status pending"
                        [attr.data-testid]="'pending-' + message.id"
                      >
                        Sending…
                      </span>
                    } @else if (!message.is_system && !selectedChat?.is_group) {
                      <mat-icon
                        *ngIf="message.fromMe"
                        class="receipt-icon"
                        [class.read]="
                          message.MessageReadReceipts && message.MessageReadReceipts.length > 0
                        "
                        [attr.aria-label]="
                          message.MessageReadReceipts?.length ? 'Read' : 'Delivered'
                        "
                        [matTooltip]="message.MessageReadReceipts?.length ? 'Read' : 'Delivered'"
                      >
                        done_all
                      </mat-icon>
                    } @else if (
                      !message.is_system &&
                      selectedChat?.is_group &&
                      message.MessageReadReceipts?.length &&
                      !message.is_deleted
                    ) {
                      <div class="read-receipts">
                        <mat-icon class="read-icon"> done_all </mat-icon>
                        {{ getReadReceiptSummary(message) }}
                      </div>
                    }
                  </div>
                </mat-card-content>
              </mat-card>
            </div>
          </ng-container>
        </div>

        @if (showJumpToLatest) {
          <button
            type="button"
            class="jump-to-latest-btn"
            (click)="jumpToLatest()"
            aria-label="Jump to latest messages"
            data-testid="jump-to-latest-button"
          >
            <mat-icon aria-hidden="true">arrow_downward</mat-icon>
            Jump to latest
          </button>
        }
      </div>

      @if (getTypingLabel(selectedChat); as typingLabel) {
        <div class="typing-status-bar" aria-live="polite" data-testid="typing-status-bar">
          <em>{{ typingLabel }}</em>
        </div>
      }

      <!-- Input Area -->
      <mat-divider></mat-divider>
      @if (selectedChat.is_disabled) {
        <div #inputAreaRef class="input-area input-area-disabled">
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="message-input">
            <input
              matInput
              [placeholder]="
                selectedChat.is_group
                  ? 'All members of the group chat must share connections with each other to continue the conversation'
                  : 'You must be connected with the other member of the chat to continue the conversation'
              "
              [disabled]="true"
              aria-label="Chat disabled"
              data-testid="message-input-disabled"
            />
          </mat-form-field>
        </div>
      } @else {
        @if (pendingAttachments.length) {
          <div class="pending-attachments">
            @for (pending of pendingAttachments; track pending.file) {
              <div class="pending-attachment" [class.error]="pending.status === 'error'">
                @if (pending.status === 'uploading') {
                  <mat-icon class="pending-spinner">hourglass_empty</mat-icon>
                } @else if (pending.status === 'error') {
                  <mat-icon>error_outline</mat-icon>
                } @else {
                  <mat-icon>{{ getAttachmentIcon(pending.attachment!) }}</mat-icon>
                }
                <span class="pending-attachment-name">{{ pending.file.name }}</span>
                <button
                  type="button"
                  mat-icon-button
                  (click)="removeAttachment(pending)"
                  aria-label="Remove attachment"
                  data-testid="remove-attachment-button"
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }
        @if (replyingToMessage) {
          <div class="reply-preview-bar" data-testid="reply-preview-bar">
            <mat-icon class="reply-preview-icon">reply</mat-icon>
            <div class="reply-preview-text">
              <span class="reply-preview-sender">{{
                getSenderName(replyingToMessage) || 'You'
              }}</span>
              <span class="reply-preview-snippet">{{
                replyingToMessage.content?.trim() || '📎 Attachment'
              }}</span>
            </div>
            <button
              type="button"
              mat-icon-button
              (click)="cancelReply()"
              aria-label="Cancel reply"
              data-testid="cancel-reply-button"
            >
              <mat-icon>close</mat-icon>
            </button>
          </div>
        }
        <div #inputAreaRef class="input-area">
          <input
            #attachmentInput
            type="file"
            multiple
            [accept]="attachmentAccept"
            hidden
            (change)="onFilesSelected($event)"
          />
          <button
            *hasPermission="permissions.MESSAGING.ATTACH_FILES"
            mat-icon-button
            type="button"
            (click)="openAttachmentPicker()"
            [disabled]="!canAddMoreAttachments"
            matTooltip="Attach files"
            aria-label="Attach files"
            data-testid="attach-file-button"
          >
            <mat-icon aria-hidden="true">attach_file</mat-icon>
          </button>
          <button
            mat-icon-button
            matTooltip="Emoji"
            aria-label="Insert emoji"
            data-testid="emoji-button"
          >
            <mat-icon aria-hidden="true">sentiment_satisfied_alt</mat-icon>
          </button>
          <mat-form-field appearance="outline" subscriptSizing="dynamic" class="message-input">
            <input
              matInput
              placeholder="Type a message..."
              [(ngModel)]="newMessage"
              (ngModelChange)="sendTyping($event.length > 0)"
              aria-label="Type a message"
              (keydown.enter)="sendMessage()"
              data-testid="message-input"
            />
          </mat-form-field>
          <button
            mat-fab
            color="primary"
            (click)="sendMessage()"
            [disabled]="
              (!newMessage.trim() && !pendingAttachments.length) || hasUploadingAttachments
            "
            aria-label="Send message"
            matTooltip="Send"
            data-testid="send-message-button"
          >
            <mat-icon aria-hidden="true">send</mat-icon>
          </button>
        </div>
      }
    </ng-container>

    <!-- No Chat Selected -->
    <ng-template #noChat>
      <div class="no-chat" role="status">
        <mat-icon aria-hidden="true">chat_bubble_outline</mat-icon>
        <h2>Welcome to Messages</h2>
        <p>Select a conversation to start chatting</p>
        <button
          mat-stroked-button
          color="primary"
          aria-label="Start a new conversation"
          (click)="openNewConversationDialog()"
          data-testid="empty-state-new-conversation-button"
        >
          <mat-icon aria-hidden="true">add</mat-icon>
          New Conversation
        </button>
      </div>
    </ng-template>
  </mat-sidenav-content>

  @if (selectedChat?.is_group) {
    <mat-sidenav mode="side" opened position="end" class="members-panel" aria-label="Group members">
      <div class="members-panel-header">Members — {{ selectedChat?.members?.length || 0 }}</div>
      <div #membersPanelListRef class="members-panel-list">
        @for (member of selectedChat?.members; track member.id) {
          <div class="member-row" [attr.data-testid]="'member-row-' + member.id">
            <div class="avatar-wrapper">
              <div class="avatar-circle">
                <app-user-avatar [imageUrl]="member.imageUrl" [name]="member.name" />
              </div>
              <span
                class="presence-dot"
                [class.online]="isUserOnline(member.id)"
                [class.offline]="!isUserOnline(member.id)"
              ></span>
            </div>
            <span class="member-name"
              ><app-user-link [userId]="member.id">{{ member.name }}</app-user-link></span
            >
          </div>
        }
      </div>
    </mat-sidenav>
  }
</mat-sidenav-container>
